import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Box, Typography, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const UserEditModal = ({ open, onClose, user, onSave }) => {
    const [formData, setFormData] = useState(user || {});
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const originalDataRef = useRef(user);

    useEffect(() => {
        if (user) {
            setFormData(user);
            setImagePreview(user.ProfileImageUrl || null);
            originalDataRef.current = user;
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
                setFormData({ ...formData, ProfileImageUrl: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleClearImage = () => {
        setImagePreview(null);
        setFormData({ ...formData, ProfileImageUrl: null });
    };

    const handleSubmit = async () => {
        setLoading(true);
        const updatedData = { UserID: formData.UserID };
        for (const key in formData) {
            if (formData[key] !== originalDataRef.current[key]) {
                updatedData[key] = formData[key];
            }
        }
        await onSave(updatedData);
        setLoading(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Edit User Profile</DialogTitle>
            <DialogContent>
                <TextField
                    label="First Name"
                    name="UserFirstName"
                    value={formData.UserFirstName || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Last Name"
                    name="UserLastName"
                    value={formData.UserLastName || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Email"
                    name="UserEmail"
                    value={formData.UserEmail || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    disabled
                />
                <TextField
                    label="Phone Number"
                    name="UserPhoneNumber"
                    value={formData.UserPhoneNumber || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Age"
                    name="UserAge"
                    type="number"
                    value={formData.UserAge || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Gender"
                    name="UserGender"
                    value={formData.UserGender || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Education Level"
                    name="UserEducationLevel"
                    value={formData.UserEducationLevel || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Occupation"
                    name="UserOccupation"
                    value={formData.UserOccupation || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Income Range"
                    name="UserIncomeRange"
                    value={formData.UserIncomeRange || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Country"
                    name="UserCountry"
                    value={formData.UserCountry || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="State"
                    name="UserState"
                    value={formData.UserState || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="City"
                    name="UserCity"
                    value={formData.UserCity || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="LinkedIn Profile Link"
                    name="UserLinkedInProfileLink"
                    value={formData.UserLinkedInProfileLink || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Facebook Profile Link"
                    name="UserFacebookProfileLink"
                    value={formData.UserFacebookProfileLink || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Instagram Profile Link"
                    name="UserInstagramProfileLink"
                    value={formData.UserInstagramProfileLink || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Twitter Profile Link"
                    name="UserTwitterProfileLink"
                    value={formData.UserTwitterProfileLink || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Current Company Link"
                    name="UserCurrentCompanyLink"
                    value={formData.UserCurrentCompanyLink || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Past Companies Link"
                    name="UserPastCompaniesLink"
                    value={formData.UserPastCompaniesLink || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Communication Email"
                    name="UserCommunicationEmail"
                    value={formData.UserCommunicationEmail || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Street 1"
                    name="UserStreet1"
                    value={formData.UserStreet1 || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Street 2"
                    name="UserStreet2"
                    value={formData.UserStreet2 || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Zip Code"
                    name="UserZip"
                    value={formData.UserZip || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Job Title"
                    name="UserJobTitle"
                    value={formData.UserJobTitle || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Company Email"
                    name="UserCompanyEmail"
                    value={formData.UserCompanyEmail || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Company Name"
                    name="UserCompanyName"
                    value={formData.UserCompanyName || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Job Industry"
                    name="UserJobIndustry"
                    value={formData.UserJobIndustry || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Professional Expertise"
                    name="UserProfessionalExpertise"
                    value={formData.UserProfessionalExpertise || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Professional Skills"
                    name="UserProfessionalSkills"
                    value={formData.UserProfessionalSkills || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Community Involvement"
                    name="UserCommunityInvolvement"
                    value={formData.UserCommunityInvolvement || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="University Attended"
                    name="UserUniversityAttended"
                    value={formData.UserUniversityAttended || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Graduation Year"
                    name="UserGraduationYear"
                    type="number"
                    value={formData.UserGraduationYear || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Leadership Program Graduated From"
                    name="UserLeadershipProgramGraduatedFrom"
                    value={formData.UserLeadershipProgramGraduatedFrom || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="UserIsToBeRegistered"
                            checked={formData.UserIsToBeRegistered || false}
                            onChange={handleChange}
                        />
                    }
                    label="Is To Be Registered"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="UserIsAlumni"
                            checked={formData.UserIsAlumni || false}
                            onChange={handleChange}
                        />
                    }
                    label="Is Alumni"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="UserIsInCurrentClass"
                            checked={formData.UserIsInCurrentClass || false}
                            onChange={handleChange}
                        />
                    }
                    label="Is In Current Class"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="UserIsChamberMember"
                            checked={formData.UserIsChamberMember || false}
                            onChange={handleChange}
                        />
                    }
                    label="Is Chamber Member"
                />
                <TextField
                    label="Leadership Batch Year"
                    name="UserLeadershipBatchYear"
                    type="number"
                    value={formData.UserLeadershipBatchYear || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Interested In Leadership Topics"
                    name="UserInterestedInLeadershipTopics"
                    value={formData.UserInterestedInLeadershipTopics || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="UserSeekingMentor"
                            checked={formData.UserSeekingMentor || false}
                            onChange={handleChange}
                        />
                    }
                    label="Seeking Mentor"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="UserWillingToBeMentor"
                            checked={formData.UserWillingToBeMentor || false}
                            onChange={handleChange}
                        />
                    }
                    label="Willing To Be Mentor"
                />
                <TextField
                    label="Mentorship Involvement List"
                    name="UserMentorshipInvolvementList"
                    value={formData.UserMentorshipInvolvementList || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            name="UserWillingToBeGuestSpeaker"
                            checked={formData.UserWillingToBeGuestSpeaker || false}
                            onChange={handleChange}
                        />
                    }
                    label="Willing To Be Guest Speaker"
                />
                <TextField
                    label="Guest Speaker Topic Preference"
                    name="UserGuestSpeakerTopicPreference"
                    value={formData.UserGuestSpeakerTopicPreference || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Profile ID"
                    name="ProfileID"
                    type="number"
                    value={formData.ProfileID || ''}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <Box marginY={2}>
                    <Typography variant="subtitle1">Update Profile Image</Typography>
                    <input
                        accept="image/*"
                        type="file"
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                        id="upload-image"
                    />
                    <label htmlFor="upload-image">
                        <Button variant="contained" component="span">
                            Upload Image
                        </Button>
                    </label>
                    {imagePreview && (
                        <Box marginTop={2}>
                            <Typography variant="body2">Image Preview:</Typography>
                            <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100px', marginTop: '8px', marginLeft: '8px' }} />
                            <IconButton onClick={handleClearImage} aria-label="clear image" style={{ marginLeft: '8px', top: 0 }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button onClick={handleSubmit} color="primary" disabled={loading}>
                    {loading ? (
                        <>
                            <CircularProgress size={20} style={{ marginRight: 8 }} color="inherit" />
                            Updating...
                        </>
                    ) : (
                        "Update"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserEditModal;